import { Card, Flex, Popover, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import { ImageVideoPreview } from "../common/ImageVideoPreview";
import { ITEM_TYPES } from "../../types";
import { ReactNode, useMemo } from "react";
import { EditButton } from "./EditButton";
import { UserCircleIcon } from "@phosphor-icons/react";
import { radius } from "@libertify/ui";

export const ItemCardPreview = ({
  cover,
  customPreview,
  title,
  onClick,
  editButton,
  item,
  previewStyle,
}: {
  cover?: {
    path?: string;
    assetId?: string;
    borderRadius?: number;
  };
  /** When set, replaces the cover image area (e.g. brand kit summary). */
  customPreview?: ReactNode;
  title?: string;
  onClick?: () => void;
  editButton?: ReactNode;
  item: string;
  previewStyle?: React.CSSProperties;
}) => {
  const { theme } = useAntTheme();

  const renderEditButton = () => {
    if (editButton) return editButton;
    if (onClick)
      return (
        <EditButton onClick={onClick} item={item} style={{ width: "100%" }} />
      );
    return null;
  };

  const imagePreview = useMemo(
    () => (
      <ImageVideoPreview
        type={ITEM_TYPES.Image}
        path={cover?.path}
        assetId={cover?.assetId}
        style={{
          objectFit: "contain",
        }}
      />
    ),
    [cover?.path, cover?.assetId]
  );

  const previewFrame = (
    <Flex
      align="center"
      justify="center"
      style={{
        overflow: "hidden",
        borderRadius: cover?.borderRadius ?? radius.box,
        backgroundColor: theme.colorBgBase,
        border: `1px solid ${theme.colorLightGrey}`,
        padding: 6,
        width: "50%",
        height: "100%",
        ...previewStyle,
      }}
    >
      {cover?.assetId ? (
        imagePreview
      ) : (
        <UserCircleIcon size={"100%"} color={theme.colorBgMask} />
      )}
    </Flex>
  );

  const renderLeftPreview = () => {
    if (customPreview) {
      return (
        <Flex
          align="flex-start"
          justify="center"
          style={{
            overflowY: "auto",
            borderRadius: radius.box,
            backgroundColor: theme.colorBgBase,
            border: `1px solid ${theme.colorLightGrey}`,
            padding: 8,
            width: "50%",
            ...previewStyle,
          }}
        >
          {customPreview}
        </Flex>
      );
    }
    if (cover?.assetId) {
      return (
        <Popover
          content={
            <div style={{ width: 200, height: "auto" }}>{imagePreview}</div>
          }
          trigger="hover"
          mouseEnterDelay={0.3}
        >
          {previewFrame}
        </Popover>
      );
    }
    return previewFrame;
  };

  return (
    <Card
      variant="borderless"
      styles={{
        body: {
          padding: 0,
        },
      }}
      style={{
        boxShadow: "none",
        width: "100%",
      }}
    >
      <Flex
        align="flex-start"
        gap={12}
        style={{
          width: "100%",
          minHeight: 120,
          ...(customPreview ? {} : { height: 120 }),
        }}
      >
        {renderLeftPreview()}

        <Flex
          vertical
          gap={20}
          style={{ width: "50%", height: "100%" }}
          align="start"
        >
          {title && (
            <Typography.Paragraph ellipsis={{ tooltip: title, rows: 3 }} strong>
              {title}
            </Typography.Paragraph>
          )}
          {renderEditButton()}
        </Flex>
      </Flex>
    </Card>
  );
};
